import { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

export default function ConnectWhatsApp() {
  const { user, selectedBusiness } = useAuth();
  const [status, setStatus] = useState('idle');
  const [msg, setMsg] = useState('');
  const [manual, setManual] = useState({ waba_id: '', access_token: '', phone_number_id: '' });
  const [showManual, setShowManual] = useState(false);
  const [connected, setConnected] = useState(false);
  const [profile, setProfile] = useState(null);
  const [verifyStatus, setVerifyStatus] = useState('idle');
  const [verifyMsg, setVerifyMsg] = useState('');
  const [profileForm, setProfileForm] = useState({ description: '', address: '', email: '', website: '' });
  const [profileMsg, setProfileMsg] = useState('');

  const FB_APP_ID = '1430190582144691';
  const bid = user?.role === 'admin' ? selectedBusiness?.id : user?.business_id;

  useEffect(() => {
    if (bid) fetchProfile();
  }, [bid]);

  useEffect(() => {
    if (window.FB) return;
    window.fbAsyncInit = function() {
      window.FB.init({ appId: FB_APP_ID, autoLogAppEvents: true, xfbml: true, version: 'v21.0' });
    };
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true; script.defer = true;
    document.body.appendChild(script);
  }, []);

  const fetchProfile = async () => {
    try {
      const r = await api.get('/connect/profile' + (user?.role === 'admin' ? '?business_id=' + bid : ''));
      if (r.data && r.data.verified_name) {
        setProfile(r.data);
        setConnected(true);
        setProfileForm({
          description: r.data.description || '',
          address: r.data.address || '',
          email: r.data.email || '',
          website: r.data.websites?.[0] || ''
        });
      }
    } catch(e) {}
  };

  const saveProfile = async () => {
    setProfileMsg('');
    try {
      await api.post('/connect/profile', { ...profileForm, business_id: bid });
      setProfileMsg('✅ Profile saved!');
      fetchProfile();
    } catch(e) {
      setProfileMsg('❌ Failed: ' + (e.response?.data?.error || e.message));
    }
  };

  const launchEmbeddedSignup = () => {
    setStatus('loading'); setMsg('');
    if (!window.FB) { setMsg('Facebook SDK not loaded.'); setStatus('error'); return; }
    window.FB.login((response) => {
      (async () => {
        if (response.authResponse?.code) {
          try {
            const r = await api.post('/connect/whatsapp', { code: response.authResponse.code, business_id: bid, session_info: response.authResponse.session_info || {} });
            if (r.data.success) { setStatus('success'); setMsg(`✅ WhatsApp Connected! WABA ID: ${r.data.waba_id}`); fetchProfile(); }
          } catch(e) { setStatus('error'); setMsg('❌ Connection failed: ' + (e.response?.data?.error || e.message)); }
        } else { setStatus('error'); setMsg('❌ Login cancelled or failed'); }
      })();
    }, { config_id: '26070060269330483', response_type: 'code', override_default_response_type: true, scope: 'whatsapp_business_management,whatsapp_business_messaging,public_profile', extras: { setup: {}, featureType: '', sessionInfoVersion: '3' } });
  };

  const saveManual = async () => {
    if (!manual.waba_id || !manual.access_token || !manual.phone_number_id) { setMsg('❌ All fields required'); return; }
    setStatus('loading');
    try {
      await api.post('/connect/manual', { ...manual, business_id: bid });
      setStatus('success'); setMsg('✅ WhatsApp Connected Successfully!'); fetchProfile();
    } catch(e) { setStatus('error'); setMsg('❌ Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const S = {
    page: { padding: 32, fontFamily: 'DM Sans, sans-serif', maxWidth: 600 },
    card: { background: '#fff', borderRadius: 16, padding: '32px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: 24 },
    inp: { width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 12 },
    btn: { width: '100%', padding: '13px', borderRadius: 10, border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer' },
    label: { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' },
  };

  return (
    <div style={S.page}>
      <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Connect WhatsApp</h2>
      <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 14 }}>Connect your WhatsApp Business account to start sending messages.</p>

      {connected && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#166534' }}>✅ WhatsApp Connected!</div>
          <div style={{ fontSize: 13, color: '#166534', marginTop: 4 }}>Phone: {profile?.display_phone_number}</div>
          <div style={{ fontSize: 13, marginTop: 4, fontWeight: 600,
            color: profile?.status === 'CONNECTED' ? '#166534' : '#d97706' }}>
            {profile?.status === 'CONNECTED' ? '✅ Verified & Active' : '⚠️ Connecting...'}
          </div>
        </div>
      )}

      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>🚀 One-Click Setup</div>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>Login with Facebook and connect your WhatsApp Business account automatically.</p>
        <button onClick={launchEmbeddedSignup} disabled={status === 'loading'} style={{ ...S.btn, background: '#1877F2', color: '#fff' }}>
          {status === 'loading' ? '⏳ Connecting...' : '🔵 Connect with Facebook'}
        </button>
      </div>

      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>⚙️ Manual Setup</div>
          <button onClick={() => setShowManual(!showManual)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>
            {showManual ? 'Hide' : 'Show'}
          </button>
        </div>
        {showManual && (
          <div>
            <input style={S.inp} placeholder="WABA ID" value={manual.waba_id} onChange={e => setManual({...manual, waba_id: e.target.value})} />
            <input style={S.inp} placeholder="Phone Number ID" value={manual.phone_number_id} onChange={e => setManual({...manual, phone_number_id: e.target.value})} />
            <textarea style={{ ...S.inp, height: 80, resize: 'vertical' }} placeholder="Access Token (EAAxxxx...)" value={manual.access_token} onChange={e => setManual({...manual, access_token: e.target.value})} />
            <button onClick={saveManual} disabled={status === 'loading'} style={{ ...S.btn, background: '#25D366', color: '#fff' }}>
              {status === 'loading' ? '⏳ Saving...' : '💾 Save Credentials'}
            </button>
          </div>
        )}
      </div>

      {msg && (
        <div style={{ padding: '14px 18px', borderRadius: 10, fontSize: 14, fontWeight: 600,
          background: status === 'success' ? '#f0fdf4' : '#fef2f2',
          color: status === 'success' ? '#166534' : '#dc2626',
          border: `1px solid ${status === 'success' ? '#bbf7d0' : '#fecaca'}` }}>
          {msg}
        </div>
      )}
    </div>
  );
}
